import { singularityStore, uiStore } from "@/store/store";
import { generateSitemap } from "../other-singularity-file";
import { addPageInWP, deleteAllPagesHeadersAndFootersFromWP } from "../wp-page-manager/pages";
import {
  loadBuilderElementsFromWordPressWithPromise,
  replaceDocumentWithAnother
} from "@/lib/wordpress/load";
import { getSectionToInsert, getSectionTypes } from "../singularity";
import { Tree } from "@/types/tree";
import { fpJsonStringify } from "@/util/fp-json-stringify";
import { copyNode, exportTree, getBlankTree, moveNode } from "@/lib/tree/tree";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { _Error, logErrorsToUiStore } from "@/lib/wordpress/data";
import * as t from "io-ts";
import { fold, right, left } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import { TemplateSettings } from "@/subapps/manage-templates/types";
import {
  BREAKDANCE_FOOTER_POST_TYPE,
  BREAKDANCE_HEADER_POST_TYPE
} from "@/lib/constants/post-types";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { SingularityMeta } from "@/types/document";
import { behindTheScenesPageGeneration } from "../behind-the-scenes-page-generation";
import { aiJobController } from "@/futurelayer/ai-job-manager/ai-jobs";
import { PageInWP } from "../wp-page-manager/types";
import { syncToDefaultWPMenuAndUseAIToDetermineWhichPagesGoInTheMenu } from "../menus/menus";

export async function step1RebuildFromUrl(rebuildFromUrl: string) {
  console.log("Rebuilding from URL:", rebuildFromUrl);

  const analysis = pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_singularity_website_intake_service",
      friendlyName: "Website Service Thing",
      dataToPost: {
        url: rebuildFromUrl
      },
      decoder: t.array(AnalyzedPageV),
      resultKey: "data"
    }),
    fold(
      () => undefined,
      data => {
        return data;
      }
    )
  );

  await step1CreatePages(
    "we are rebuilding an existing website, and here is the info on the existing website. we want the new website to be as similar as possible to the existing one. here is data on the existing website: " +
      jsonStringifyOrUndefined(analysis)
  );
}

export const AnalyzedPageV = t.type({
  url: t.string,
  originalTitle: t.string,
  analysis: t.union([
    t.null,
    t.type({
      title: t.string,
      sections: t.array(
        t.type({
          title: t.string,
          content: t.string
        })
      )
    })
  ]),
  images: t.type({
    images: t.array(
      t.type({
        url: t.string,
        description: t.string
      })
    )
  }),
  error: t.union([t.undefined, t.string])
});

export type AnalyzedPage = t.TypeOf<typeof AnalyzedPageV>;

export async function step1CreatePages(additionalPromptInfo = "") {
  await deleteAllPagesHeadersAndFootersFromWP();

  const sitemap = await generateSitemap(additionalPromptInfo);

  if (!sitemap) return;


  const createdPages : PageInWP[] = [];

  for (const page of sitemap) {
    const pageData = await addPageInWP(
      page.title,
      {
        singularityInitialSections: page.sections
      },
      "page"
    );

    if (!pageData) continue;

    createdPages.push(pageData);

    if (!page.title.toLowerCase().includes("home")) {
      aiJobController.addJob(
        async abortController => {
          await behindTheScenesPageGeneration(pageData.id, abortController);
        },
        {
          jobType: 'background_page_generation',
          pageId: pageData.id,
          pageTitle: page.title,
          description: `Behind the scenes page generation for "${page.title}"`,
          priority: 'low',
        }
      );
    }

  }

  await syncToDefaultWPMenuAndUseAIToDetermineWhichPagesGoInTheMenu(createdPages);

  await step1CreateHeaderAndFooter();

  // page title contains "home"
  const maybeHomepage = singularityStore.pagesInWP.find(page =>
    page.title.toLowerCase().includes("home")
  );

  if (maybeHomepage) {
    uiStore.setUnsavedChangesPresent(false);
    await replaceDocumentWithAnother(maybeHomepage.id);
  } else {
    uiStore.setUnsavedChangesPresent(false);
    await replaceDocumentWithAnother(singularityStore.pagesInWP[0].id);
  }

  singularityStore.setSingularityUiState({
    ...singularityStore.singularityUiState,
    singularityLeftPanel: "page_structure"
  });

  /*
  should i support mass adding the pages? instead of individual AJAX requests for each page?
  well, that would be time consuming
  i need a way to generate the pages in the background
  */
}

export async function step1CreateHeaderAndFooter() {
  await createHeaderOrFooter("Header", BREAKDANCE_HEADER_POST_TYPE, "head");
  await createHeaderOrFooter("Footer", BREAKDANCE_FOOTER_POST_TYPE, "foot");
}

async function createHeaderOrFooter(
  title: string,
  postType:
    | typeof BREAKDANCE_HEADER_POST_TYPE
    | typeof BREAKDANCE_FOOTER_POST_TYPE,
  templateCategorySearchQuery: string
) {
  const headerOrFooter = await addPageInWP(title, {}, postType);
  if (!headerOrFooter) return;

  const sectionType = getSectionTypes().find(type =>
    type.toLowerCase().includes(templateCategorySearchQuery)
  );
  if (!sectionType) return;

  const sectionToInsert = getSectionToInsert({
    type: sectionType,
    sectionFriendlyName: title,
    description: ""
  });
  if (!sectionToInsert) return;

  await loadBuilderElementsFromWordPressWithPromise();

  const blankTree = getBlankTree();

  const copiedNode = copyNode(blankTree, sectionToInsert);
  moveNode(blankTree, copiedNode.id, blankTree.root.id, 0);

  const templateSettings: TemplateSettings = {
    type: "everywhere",
    priority: 1,
    ruleGroups: [],
    disabled: false
  };

  saveTreeToWP(blankTree, headerOrFooter.id, templateSettings);
}

export async function saveTreeToWP(
  tree: Tree,
  pageId: number,
  templateSettings: undefined | TemplateSettings
) {
  const dataToPost: Record<string, unknown> = {
    id: pageId,
    tree: fpJsonStringify(exportTree(tree)),
    templateSettings: templateSettings
      ? fpJsonStringify(templateSettings)
      : undefined
  };

  const result = pipe(
    await makeWordPressAjaxRequest({
      friendlyName: "Saving to WordPress",
      action: "breakdance_save",
      dataToPost,
      decoder: t.unknown
    }),
    fold(
      (errors: _Error[]) => {
        logErrorsToUiStore(errors);
        return left(errors);
      },
      r => {
        return right(r);
      }
    )
  );
  return result;
}



export async function updateSingularityMeta(
  pageId: number,
  singularityMeta: SingularityMeta
) {
  const dataToPost: Record<string, unknown> = {
    id: pageId,
    singularityMeta: singularityMeta
      ? fpJsonStringify(singularityMeta)
      : undefined
  };

  const result = pipe(
    await makeWordPressAjaxRequest({
      friendlyName: "Update Singularity Meta",
      action: "breakdance_singularity_update_singularity_meta",
      dataToPost,
      decoder: t.unknown
    }),
    fold(
      (errors: _Error[]) => {
        logErrorsToUiStore(errors);
        return left(errors);
      },
      r => {
        return right(r);
      }
    )
  );
  return result;
}
